import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useObserverStore, useRuntimeStore } from "../store";

function statusClassName(status: string): string {
  switch (status) {
    case "pending-approval":
      return "border-warning/40 bg-warning-bg text-warning";
    case "approved":
    case "started":
      return "border-brand/30 bg-brand/10 text-brand-light";
    case "completed":
      return "border-success/30 bg-success-bg text-success";
    case "rejected":
    case "timed-out":
    case "failed":
      return "border-danger/30 bg-danger-bg text-danger";
    default:
      return "border-border bg-surface text-zinc-400";
  }
}

export function ObserverPanel() {
  const connected = useObserverStore((state) => state.connected);
  const sessions = useObserverStore((state) => state.sessions);
  const approveExecution = useObserverStore((state) => state.approveExecution);
  const rejectExecution = useObserverStore((state) => state.rejectExecution);
  const runtimeReachable = useRuntimeStore((state) => state.reachable);
  const runtimePaired = useRuntimeStore((state) => state.paired);
  const [open, setOpen] = useState(false);
  const orderedSessions = Object.entries(sessions).sort(([, left], [, right]) => {
    const leftUpdatedAt = left[0]?.updatedAt ?? 0;
    const rightUpdatedAt = right[0]?.updatedAt ?? 0;
    return rightUpdatedAt - leftUpdatedAt;
  });

  useEffect(() => {
    setOpen(connected);
  }, [connected]);

  const promptReject = (executionId: string) => {
    const reason = window.prompt("Reject reason", "Need human review first");
    if (reason === null) return;

    const trimmedReason = reason.trim();
    if (!trimmedReason) return;
    rejectExecution(executionId, trimmedReason);
  };

  const renderDiscoveryState = () => {
    if (!runtimeReachable) {
      return (
        <div className="space-y-3 px-4 py-6 text-sm text-zinc-400">
          <div className="font-medium text-zinc-200">No local runtime connected.</div>
          <p>
            Start the local runtime or open the desktop app, then pair this browser surface.
            Once an observer connection is live, this panel opens automatically and shows CLI and MCP activity.
          </p>
        </div>
      );
    }

    if (!runtimePaired) {
      return (
        <div className="space-y-3 px-4 py-6 text-sm text-zinc-400">
          <div className="font-medium text-zinc-200">Local runtime is reachable, but not paired.</div>
          <p>
            Pair this browser surface with the local runtime from Local Apps. After pairing,
            Spuerhund Web can subscribe as an observer and approve agent-driven follow-up queries.
          </p>
        </div>
      );
    }

    if (!connected) {
      return (
        <div className="space-y-3 px-4 py-6 text-sm text-zinc-400">
          <div className="font-medium text-zinc-200">Waiting for observer connection.</div>
          <p>
            This surface will auto-open the panel as soon as the observer websocket is connected.
            Keep Spürhund Web or Desktop open while CLI and MCP queries run.
          </p>
        </div>
      );
    }

    return (
      <div className="px-4 py-6 text-sm text-zinc-500">
        No observed sessions yet.
      </div>
    );
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="hidden w-12 shrink-0 items-center justify-center border-l border-border bg-surface-raised text-zinc-400 transition hover:bg-surface hover:text-zinc-200 lg:flex"
        title="Open observer panel"
        aria-label="Open observer panel"
      >
        <div className="flex items-center gap-2 [writing-mode:vertical-rl]">
          <ChevronLeft size={14} />
          <span
            className={`h-2 w-2 rounded-full ${
              connected
                ? "bg-success"
                : runtimeReachable && runtimePaired
                  ? "bg-warning"
                  : "bg-zinc-600"
            }`}
          />
          <span className="text-[10px] font-semibold uppercase tracking-[0.18em]">Observer</span>
        </div>
      </button>
    );
  }

  return (
    <aside className="hidden w-[22rem] shrink-0 flex-col border-l border-border bg-surface-raised lg:flex">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-zinc-100">Observer</div>
            <div className="text-xs text-zinc-500">Runtime activity across CLI, MCP, and UI</div>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${
                connected
                  ? "border-success/30 bg-success-bg text-success"
                  : runtimeReachable && runtimePaired
                    ? "border-warning/30 bg-warning-bg text-warning"
                    : "border-border bg-surface text-zinc-500"
              }`}
            >
              {connected ? "Live" : runtimeReachable && runtimePaired ? "Waiting" : "Offline"}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-md border border-border p-1 text-zinc-500 transition hover:bg-surface hover:text-zinc-200"
              title="Hide observer panel"
              aria-label="Hide observer panel"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        {!connected || orderedSessions.length === 0 ? (
          renderDiscoveryState()
        ) : (
          orderedSessions.map(([sessionId, executions]) => (
            <section key={sessionId} className="border-b border-border px-4 py-3">
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
                {sessionId}
              </div>
              <div className="space-y-2">
                {executions.map((execution) => (
                  <article key={execution.executionId} className="rounded-xl border border-border bg-surface px-3 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-medium text-zinc-100">
                          {(execution.source ?? "runtime").toUpperCase()} · {(execution.requestType ?? "query").toUpperCase()}
                        </div>
                        <div className="mt-1 text-[11px] text-zinc-500">
                          {execution.intent ?? "unknown"} · {execution.approvalMode ?? "none"}
                        </div>
                      </div>
                      <div className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${statusClassName(execution.status)}`}>
                        {execution.status}
                      </div>
                    </div>

                    {(execution.reason || execution.errorMessage) && (
                      <div className="mt-2 rounded-lg border border-border bg-surface-raised px-2.5 py-2 text-xs text-zinc-300">
                        {execution.reason ?? execution.errorMessage}
                      </div>
                    )}

                    {execution.status === "pending-approval" && (
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => approveExecution(execution.executionId)}
                          className="rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-brand-light"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => promptReject(execution.executionId)}
                          className="rounded-lg border border-danger/40 bg-danger-bg px-3 py-1.5 text-xs font-semibold text-danger hover:border-danger/60"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </aside>
  );
}
